/**
 * What an account is on one programme, which decides the editor it gets.
 *
 * The shell's presentation profile is one role across every programme, so a
 * lead of one programme who builds modules on another read as a lead on both.
 *
 * @module components/shared/programme-role
 */

import type { SessionContext } from "@/platform";

export type ProgrammeRole = "admin" | "lead" | "builder" | "other";

export function programmeRoleFor(
  session: SessionContext | null,
  programmeId: string | undefined,
): ProgrammeRole {
  if (!session?.subject) {
    return "other";
  }
  const { accountRole, responsibilities } = session.subject;
  if (accountRole === "client-admin" || accountRole === "platform-admin") {
    return "admin";
  }
  const held = new Set(
    responsibilities
      .filter((assignment) => assignment.programmeId === programmeId)
      .map((assignment) => assignment.responsibility),
  );
  if (held.has("lead")) {
    return "lead";
  }
  // The builder's layout hides Governance, so a builder who also reviews or
  // approves here takes the full editor to reach it.
  if (held.has("reviewer") || held.has("approver")) {
    return "other";
  }
  return held.has("builder") ? "builder" : "other";
}

/** The surfaces a module builder reaches; the rest are the lead's. */
export const BUILDER_SURFACES: readonly string[] = ["overview", "studio", "team"];
